import ui from "./ui.js";
import materials from "./materials.js";
import * as THREE from "three";

// ─── ИМЕНА МАТЕРИАЛОВ В МОДЕЛИ ───────────────────────────────────────────────
const WOOD_MAT_NAMES    = ['wood']
const FRAME_MAT_NAMES   = ['frame colors']
const LEATHER_MAT_NAMES = ['leather']
const BOTTOM_MAT_NAMES  = ['bottom color']

// ─────────────────────────────────────────────────────────────────────────────

export default function options(model, _unused, scene, camera, gui) {
    ui()

    const mats = materials(scene)

    // ── Кэш мешей по группам (заполняется один раз по исходным именам) ────────
    function buildMeshCache(nameList) {
        const meshes = []
        model.traverse(child => {
            if (!child.isMesh || !child.material) return
            const match = mat => nameList.some(n => mat.name.toLowerCase() === n.toLowerCase())
            if (Array.isArray(child.material)) {
                child.material.forEach((m, idx) => {
                    if (match(m)) meshes.push({ child, idx })
                })
            } else if (match(child.material)) {
                meshes.push({ child, idx: null })
            }
        })
        return meshes
    }

    function applyMatToCache(cache, newMat) {
        cache.forEach(({ child, idx }) => {
            if (idx !== null) {
                const arr = child.material.slice()
                arr[idx] = newMat
                child.material = arr
            } else {
                child.material = newMat
            }
        })
    }

    function applyColorToCache(cache, hexColor) {
        const color = new THREE.Color(hexColor)
        cache.forEach(({ child, idx }) => {
            const mat = idx !== null ? child.material[idx] : child.material
            if (mat) mat.color.set(color)
        })
    }

    // Дебаг: какие UV-атрибуты есть на вудовых мешах
    model.traverse(child => {
        if (!child.isMesh) return
        const mat = Array.isArray(child.material) ? child.material : [child.material]
        if (!mat.some(m => m?.name.toLowerCase() === 'wood')) return
        const attrs = Object.keys(child.geometry.attributes)
        console.log(`[WOOD MESH] ${child.name} | attrs: ${attrs.join(', ')}`)
    })

    const woodCache    = buildMeshCache(WOOD_MAT_NAMES)
    const frameCache   = buildMeshCache(FRAME_MAT_NAMES)
    const leatherCache = buildMeshCache(LEATHER_MAT_NAMES)
    const bottomCache  = buildMeshCache(BOTTOM_MAT_NAMES)

    // Художник: bottom не должен иметь normalMap (сильно бликует)
    bottomCache.forEach(({ child, idx }) => {
        const mat = idx !== null ? child.material[idx] : child.material
        if (!mat) return
        mat.normalMap = null
        mat.normalScale?.set(0, 0)
        mat.needsUpdate = true
    })

    // ── WOOD ─────────────────────────────────────────────────────────────────
    const woodMap = { dark: mats.wood.dark, gray: mats.wood.gray, light: mats.wood.light, mid: mats.wood.mid }

    // Применяем первый checked вариант сразу
    const defaultWood = document.querySelector('input[name="woodMaterial"]:checked')
    if (defaultWood) applyMatToCache(woodCache, woodMap[defaultWood.value])

    document.querySelectorAll('input[name="woodMaterial"]').forEach(item => {
        item.addEventListener('change', e => applyMatToCache(woodCache, woodMap[e.target.value]))
    })

    // ── FRAME COLOR ───────────────────────────────────────────────────────────
    const frameMap = {
        c1: mats.frame.c1, c2: mats.frame.c2, c3: mats.frame.c3, c4: mats.frame.c4,
        c5: mats.frame.c5, c6: mats.frame.c6, c7: mats.frame.c7, c8: mats.frame.c8, c9: mats.frame.c9,
    }

    const defaultFrame = document.querySelector('input[name="frameColor"]:checked')
    if (defaultFrame) applyMatToCache(frameCache, frameMap[defaultFrame.value])

    document.querySelectorAll('input[name="frameColor"]').forEach(item => {
        item.addEventListener('change', e => applyMatToCache(frameCache, frameMap[e.target.value]))
    })

    // ── LEATHER (sidebar swatches) ────────────────────────────────────────────
    const defaultLeather = document.querySelector('input[name="leatherColor"]:checked')
    if (defaultLeather) applyColorToCache(leatherCache, defaultLeather.value)

    document.querySelectorAll('input[name="leatherColor"]').forEach(item => {
        item.addEventListener('change', e => {
            applyColorToCache(leatherCache, e.target.value)
            if (leatherCtrl) leatherCtrl.setValue(e.target.value)
        })
    })

    // ── BOTTOM COLOR (sidebar swatches) ──────────────────────────────────────
    const defaultBottom = document.querySelector('input[name="bottomColor"]:checked')
    if (defaultBottom) applyColorToCache(bottomCache, defaultBottom.value)

    document.querySelectorAll('input[name="bottomColor"]').forEach(item => {
        item.addEventListener('change', e => {
            applyColorToCache(bottomCache, e.target.value)
            if (bottomCtrl) bottomCtrl.setValue(e.target.value)
        })
    })

    // ── CUSTOM COLOR PICKERS (gradient swatch → native color picker) ─────────
    document.querySelectorAll('.custom-color-input').forEach(input => {
        const label = input.closest('.color-radio')
        const target = input.dataset.target
        const cache = target === 'leatherColor' ? leatherCache : bottomCache
        const radios = document.querySelectorAll(`input[name="${target}"]`)

        const panel = label.closest('.panel')
        const dataSpan = panel?.previousElementSibling?.querySelector('.accordion-data')

        const activate = () => {
            radios.forEach(r => { r.checked = false })
            document.querySelectorAll(`.custom-color-input[data-target="${target}"]`)
                .forEach(i => i.closest('.color-radio').classList.remove('active'))
            label.classList.add('active')
            if (dataSpan) dataSpan.textContent = 'Custom'
        }

        input.addEventListener('input', e => {
            activate()
            applyColorToCache(cache, e.target.value)
            if (target === 'leatherColor' && leatherCtrl) leatherCtrl.setValue(e.target.value)
            if (target === 'bottomColor' && bottomCtrl) bottomCtrl.setValue(e.target.value)
        })

        radios.forEach(r => r.addEventListener('change', () => label.classList.remove('active')))
    })

    // ── lil-gui: только color picker для тонкой настройки ────────────────────
    let leatherCtrl, bottomCtrl

    if (gui) {
        const matFolder = gui.addFolder('Материалы')

        const guiState = { leather: '#f0ede5', bottom: '#f2f0ec' }

        leatherCtrl = matFolder.addColor(guiState, 'leather')
            .name('Leather')
            .onChange(v => applyColorToCache(leatherCache, v))

        bottomCtrl = matFolder.addColor(guiState, 'bottom')
            .name('Bottom')
            .onChange(v => applyColorToCache(bottomCache, v))

        matFolder.open()
    }
}
